import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  FormHelperText,
  FormControlLabel,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import {
  useAgentProfileCreateControllerAgentProfileCreateForManagersMutation,
  useAgentProfileDeleteControllerAgentProfileDeleteForManagersMutation,
  useAgentProfileEditControllerAgentProfileEditForManagersMutation,
  useAgentProfileListControllerAgentProfileListForAdminsQuery,
  TaskFlowDto,
} from '@shared/services/apiService/apiService';
import React, { useRef, useState } from 'react';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { MarkdownEditor } from '@shared/components/MarkdownEditor';
import { toast } from 'react-toastify';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import { TableEmptyRow } from '@shared/components/listPage';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import { showProfileConflictToast } from '@shared/utils/agentProfileConflictMessage';
import {
  TaskFlowPanel,
  TaskFlowPanelHandle,
  TaskFlowSaveState,
} from '@features/Admin/Agent/ui/TaskFlowCanvas';

interface AgentProfilesTabContentProps {
  companyId?: string;
}

interface AgentProfileFormData {
  name: string;
  prompt: string;
  firstMessage: string;
  firstMessageInterruptionsEnabled: boolean;
}

interface FormErrors {
  name?: string;
  prompt?: string;
}

export const AgentProfilesTabContent: React.FC<AgentProfilesTabContentProps> = ({ companyId }) => {
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [editTab, setEditTab] = useState(0);
  const taskFlowPanelRef = useRef<TaskFlowPanelHandle>(null);
  const [taskFlowSaveState, setTaskFlowSaveState] = useState<TaskFlowSaveState>({
    canSave: false,
    isSaving: false,
  });
  const [currentProfile, setCurrentProfile] = useState<any>(null);
  const [formData, setFormData] = useState<AgentProfileFormData>({
    name: '',
    prompt: '',
    firstMessage: '',
    firstMessageInterruptionsEnabled: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const { t } = useTranslation();
  const { showError } = useErrorHandler();

  const translations = {
    prompt: t('superAdminMainPage.companies.profiles.prompt'),
    openingLine: t('superAdminMainPage.companies.profiles.openingLine'),
    openingLinePlaceholder: t('superAdminMainPage.companies.profiles.openingLinePlaceholder'),
    firstMessageInterruptionsEnabled: t(
      'superAdminMainPage.companies.profiles.firstMessageInterruptionsEnabled',
    ),
    profileName: t('superAdminMainPage.companies.profiles.profileName'),
    agentProfilesDescription: t('superAdminMainPage.companies.profiles.description'),
    createNewProfile: t('superAdminMainPage.companies.profiles.createNewProfile'),
    noAgentProfilesFound: t('superAdminMainPage.companies.profiles.noAgentProfilesFound'),
    name: t('superAdminMainPage.companies.profiles.name'),
    actions: t('superAdminMainPage.companies.profiles.actions'),
    createAgentProfile: t('superAdminMainPage.companies.profiles.createAgentProfile'),
    editAgentProfile: t('superAdminMainPage.companies.profiles.editAgentProfile'),
    deleteAgentProfile: t('superAdminMainPage.companies.profiles.deleteAgentProfile'),
    cancel: t('superAdminMainPage.companies.profiles.cancel'),
    create: t('superAdminMainPage.companies.profiles.create'),
    save: t('superAdminMainPage.companies.profiles.save'),
    delete: t('superAdminMainPage.companies.profiles.delete'),
    nameRequired: t('superAdminMainPage.companies.profiles.nameRequired'),
    promptRequired: t('superAdminMainPage.companies.profiles.promptRequired'),
    deleteConfirmation: t('superAdminMainPage.companies.profiles.deleteConfirmation'),
  };

  const {
    data: agentProfiles,
    isLoading,
    refetch,
  } = useAgentProfileListControllerAgentProfileListForAdminsQuery(
    { id: companyId || '' },
    { skip: !companyId },
  );

  const [createAgentProfile, { isLoading: isCreating }] =
    useAgentProfileCreateControllerAgentProfileCreateForManagersMutation();
  const [editAgentProfile, { isLoading: isEditing }] =
    useAgentProfileEditControllerAgentProfileEditForManagersMutation();
  const [deleteAgentProfile, { isLoading: isDeleting }] =
    useAgentProfileDeleteControllerAgentProfileDeleteForManagersMutation();

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = translations.nameRequired;
    }

    if (!formData.prompt.trim()) {
      newErrors.prompt = translations.promptRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenCreateDialog = () => {
    setFormData({
      name: '',
      prompt: '',
      firstMessage: '',
      firstMessageInterruptionsEnabled: false,
    });
    setErrors({});
    setOpenCreateDialog(true);
  };

  const handleOpenEditDialog = (profile: any) => {
    setCurrentProfile(profile);
    setEditTab(0);
    setTaskFlowSaveState({ canSave: false, isSaving: false });
    setFormData({
      name: profile.name || '',
      prompt: profile.prompt || '',
      firstMessage: profile.firstMessage ?? '',
      firstMessageInterruptionsEnabled: profile.firstMessageInterruptionsEnabled ?? false,
    });
    setErrors({});
    setOpenEditDialog(true);
  };

  const handleOpenDeleteConfirm = (profile: any) => {
    setCurrentProfile(profile);
    setOpenDeleteConfirm(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handlePromptChange = (value: string) => {
    setFormData((prev) => ({ ...prev, prompt: value }));

    if (errors.prompt) {
      setErrors((prev) => ({ ...prev, prompt: undefined }));
    }
  };

  const handleFirstMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, firstMessage: e.target.value }));
  };

  const handleCreateProfile = async () => {
    if (!companyId || !validateForm()) return;

    try {
      const normalizedFirstMessage = formData.firstMessage.trim();

      await createAgentProfile({
        id: companyId,
        agentProfileCreateAdminRequestDto: {
          name: formData.name,
          prompt: formData.prompt,
          firstMessage: normalizedFirstMessage === '' ? null : normalizedFirstMessage,
          firstMessageInterruptionsEnabled: formData.firstMessageInterruptionsEnabled,
        },
      }).unwrap();

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('common.success'),
          description: t('superAdminMainPage.companies.profiles.messages.created', {
            name: formData.name,
          }),
        },
      });

      setOpenCreateDialog(false);
      refetch();
    } catch (error) {
      showError({
        title: t('superAdminMainPage.companies.profiles.errors.createTitle'),
        error,
        suggestion: t('superAdminMainPage.companies.profiles.errors.createSuggestion'),
      });
    }
  };

  const handleEditProfile = async () => {
    if (!companyId || !currentProfile?.id || !validateForm()) return;

    try {
      const normalizedFirstMessage = formData.firstMessage.trim();

      await editAgentProfile({
        id: companyId,
        profileId: currentProfile.id,
        agentProfileEditRequestDto: {
          name: formData.name,
          prompt: formData.prompt,
          firstMessage: normalizedFirstMessage === '' ? null : normalizedFirstMessage,
          firstMessageInterruptionsEnabled: formData.firstMessageInterruptionsEnabled,
        },
      }).unwrap();

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('common.success'),
          description: t('superAdminMainPage.companies.profiles.messages.updated', {
            name: formData.name,
          }),
        },
      });

      setOpenEditDialog(false);
      refetch();
    } catch (error) {
      showError({
        title: t('superAdminMainPage.companies.profiles.errors.updateTitle'),
        error,
        suggestion: t('superAdminMainPage.companies.profiles.errors.updateSuggestion'),
      });
    }
  };

  const handleDeleteProfile = async () => {
    if (!currentProfile?.id || !companyId) return;

    try {
      await deleteAgentProfile({ id: companyId, profileId: currentProfile.id }).unwrap();

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('common.success'),
          description: t('superAdminMainPage.companies.profiles.messages.deleted', {
            name: currentProfile.name,
          }),
        },
      });

      setOpenDeleteConfirm(false);
      refetch();
    } catch (error) {
      if (
        showProfileConflictToast(
          error,
          t,
          t('superAdminMainPage.companies.profiles.errors.deleteTitle'),
          t('superAdminMainPage.companies.profiles.errors.deleteSuggestion'),
        )
      ) {
        return;
      }
      showError({
        title: t('superAdminMainPage.companies.profiles.errors.deleteTitle'),
        error,
        suggestion: t('superAdminMainPage.companies.profiles.errors.deleteSuggestion'),
      });
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 2,
        }}>
        <Typography variant="body2" color="text.secondary">
          {translations.agentProfilesDescription}
        </Typography>
        <Button
          variant="contained"
          startIcon={<IconPlus size={18} />}
          onClick={handleOpenCreateDialog}
          disabled={!companyId}
          sx={{ whiteSpace: 'nowrap' }}>
          {translations.createNewProfile}
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: `${theme.shape.borderRadius}px`,
          boxShadow: 'none',
        })}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{translations.name}</TableCell>
              <TableCell align="right">{translations.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {agentProfiles?.map((profile) => (
              <TableRow key={profile.id} hover>
                <TableCell>
                  <Typography fontWeight={600}>{profile.name}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={translations.editAgentProfile}>
                    <IconButton size="small" onClick={() => handleOpenEditDialog(profile)}>
                      <IconPencil size={18} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={translations.deleteAgentProfile}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleOpenDeleteConfirm(profile)}>
                      <IconTrash size={18} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && !agentProfiles?.length && (
              <TableEmptyRow colSpan={2} message={translations.noAgentProfilesFound} />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        maxWidth="lg"
        fullWidth>
        <DialogTitle>{translations.createAgentProfile}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label={translations.profileName}
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleInputChange}
            error={!!errors.name}
            required
            sx={{ mb: errors.name ? 0.5 : 2 }}
          />
          {errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
          <MarkdownEditor
            value={formData.prompt}
            onChange={handlePromptChange}
            label={translations.prompt}
            error={!!errors.prompt}
            helperText={errors.prompt}
            required
            height={600}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {translations.openingLine}
            </Typography>
            <TextField
              margin="dense"
              name="firstMessage"
              type="text"
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              placeholder={translations.openingLinePlaceholder}
              value={formData.firstMessage}
              onChange={handleFirstMessageChange}
            />
          </Box>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <CustomSwitch
                sx={{ ml: '20px', mr: '14px' }}
                checked={formData.firstMessageInterruptionsEnabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({
                    ...prev,
                    firstMessageInterruptionsEnabled: e.target.checked,
                  }))
                }
              />
            }
            label={translations.firstMessageInterruptionsEnabled}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>{translations.cancel}</Button>
          <Button
            onClick={handleCreateProfile}
            variant="contained"
            color="primary"
            disabled={isCreating}>
            {isCreating ? <CircularProgress size={24} /> : translations.create}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            height: { xs: '100%', md: '90vh' },
            maxHeight: { xs: '100%', md: '90vh' },
            m: { xs: 0, md: 2 },
          },
        }}>
        <DialogTitle>{translations.editAgentProfile}</DialogTitle>
        <Tabs
          value={editTab}
          onChange={(_, value) => setEditTab(value)}
          sx={{ px: { xs: 1, sm: 3 }, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Tab label="General" />
          <Tab label={t('assistantsPage.modals.tabs.taskFlow', { defaultValue: 'Task Flow' })} />
        </Tabs>
        <DialogContent sx={{ minHeight: 0, overflow: 'auto' }}>
          {editTab === 0 ? (
            <>
              <TextField
                autoFocus
                margin="dense"
                name="name"
                label={translations.profileName}
                type="text"
                fullWidth
                variant="outlined"
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                required
                sx={{ mb: errors.name ? 0.5 : 2 }}
              />
              {errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
              <MarkdownEditor
                value={formData.prompt}
                onChange={handlePromptChange}
                label={translations.prompt}
                error={!!errors.prompt}
                helperText={errors.prompt}
                required
                height={600}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {translations.openingLine}
                </Typography>
                <TextField
                  margin="dense"
                  name="firstMessage"
                  type="text"
                  fullWidth
                  multiline
                  minRows={3}
                  variant="outlined"
                  placeholder={translations.openingLinePlaceholder}
                  value={formData.firstMessage}
                  onChange={handleFirstMessageChange}
                />
              </Box>
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <CustomSwitch
                    sx={{ ml: '20px', mr: '14px' }}
                    checked={formData.firstMessageInterruptionsEnabled}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData((prev) => ({
                        ...prev,
                        firstMessageInterruptionsEnabled: e.target.checked,
                      }))
                    }
                  />
                }
                label={translations.firstMessageInterruptionsEnabled}
              />
            </>
          ) : currentProfile ? (
            <TaskFlowPanel
              ref={taskFlowPanelRef}
              initialTaskFlow={currentProfile.taskFlow}
              readOnly={false}
              profileName={currentProfile.name}
              onSaveStateChange={setTaskFlowSaveState}
              onSave={async (taskFlow: TaskFlowDto | null) => {
                if (!companyId || !currentProfile?.id) return null;
                const updatedProfile = await editAgentProfile({
                  id: companyId,
                  profileId: currentProfile.id,
                  agentProfileEditRequestDto: { taskFlow },
                }).unwrap();
                setCurrentProfile(updatedProfile);
                refetch();
                return updatedProfile.taskFlow;
              }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>{translations.cancel}</Button>
          {editTab === 1 && (
            <Button
              onClick={() => void taskFlowPanelRef.current?.save()}
              variant="contained"
              color="primary"
              disabled={!taskFlowSaveState.canSave}>
              {taskFlowSaveState.isSaving
                ? t('assistantsPage.modals.taskFlow.saving')
                : t('assistantsPage.modals.taskFlow.save')}
            </Button>
          )}
          {editTab === 0 && (
            <Button
              onClick={handleEditProfile}
              variant="contained"
              color="primary"
              disabled={isEditing}>
              {isEditing ? <CircularProgress size={24} /> : translations.save}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
        <DialogTitle>{translations.deleteAgentProfile}</DialogTitle>
        <DialogContent>
          <Typography>
            {translations.deleteConfirmation.replace('{name}', currentProfile?.name || '')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)}>{translations.cancel}</Button>
          <Button
            onClick={handleDeleteProfile}
            variant="contained"
            color="error"
            disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={24} /> : translations.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
